import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useGetDraftSearchTextsQuery } from '@/api/chat/getDraftSearchTexts';
import { useEventCallback } from '@/hooks/useEventCallback';
import {
  applyActiveSearchMatch,
  countMatches,
  draftMarkdownToSearchText,
  getMessageMatchIndices,
  type MatchIndex,
} from '@/utils/chatSearch';
import type { ChatMessage } from '@/types/chat';
import { getDraftVersionIds } from '../utils/sourcesPanelCitations';

export interface UseChatSearchOptions {
  messages: ChatMessage[];
  messageRefs: React.RefObject<Map<string, HTMLDivElement | null>>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  /** Matches the editor found in the open draft; they count unsaved edits too. */
  openDraftMatchCount?: OpenDraftMatchCount | null;
  /** Called when the active match moves into a draft, to open it. */
  onActiveDraftMatch?: (draftVersionId: string) => void;
}

/** The editor's match count for the open draft, and the query it counted. */
export interface OpenDraftMatchCount {
  draftVersionId: string;
  query: string;
  count: number;
}

/** The active match when it is in a draft: the draft and the match's ordinal inside it. */
export interface ActiveDraftMatch {
  draftVersionId: string;
  index: number;
}

export interface UseChatSearchReturn {
  isSearchOpen: boolean;
  searchQuery: string;
  activeMatchIndex: number;
  matchIndices: MatchIndex[];
  activeDraftMatch: ActiveDraftMatch | null;
  hasMatches: boolean;
  totalMatches: number;
  openSearch: () => void;
  closeSearch: () => void;
  /** Closes the search and puts focus back on the control that opened it. */
  dismissSearch: () => void;
  /** Callback ref for the header's search button, which unmounts while the search is open. */
  attachSearchButton: (node: HTMLButtonElement | null) => void;
  setSearchQuery: (q: string) => void;
  goToNextMatch: () => void;
  goToPrevMatch: () => void;
  clearSearch: () => void;
}

export function useChatSearch({
  messages,
  scrollContainerRef,
  openDraftMatchCount,
  onActiveDraftMatch,
}: UseChatSearchOptions): UseChatSearchReturn {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQueryState] = useState('');
  const [activeMatchIndexState, setActiveMatchIndex] = useState(0);

  const setSearchQuery = useCallback((q: string) => {
    setSearchQueryState(q);
    setActiveMatchIndex(0);
  }, []);

  const draftVersionIds = useMemo(() => getDraftVersionIds(messages), [messages]);
  const { data: draftMarkdownById } = useGetDraftSearchTextsQuery(
    { draftVersionIds },
    { skip: !isSearchOpen || draftVersionIds.length === 0 },
  );
  const draftTextById = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(draftMarkdownById ?? {}).map(([id, markdown]) => [id, draftMarkdownToSearchText(markdown)]),
      ),
    [draftMarkdownById],
  );
  const draftMatchCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [id, text] of Object.entries(draftTextById)) counts[id] = countMatches(text, searchQuery);
    // A count for the previous query is stale until the editor recounts.
    if (openDraftMatchCount?.query === searchQuery)
      counts[openDraftMatchCount.draftVersionId] = openDraftMatchCount.count;
    return counts;
  }, [draftTextById, searchQuery, openDraftMatchCount]);

  const matchIndices = useMemo(
    () => getMessageMatchIndices(messages, searchQuery, draftMatchCounts),
    [messages, searchQuery, draftMatchCounts],
  );
  const totalMatches = matchIndices.length;
  const hasMatches = totalMatches > 0;
  // Draft counts can drop (an edit, or the editor counting differently), so keep the index in range.
  const activeMatchIndex = Math.min(activeMatchIndexState, Math.max(totalMatches - 1, 0));
  const activeMatch = matchIndices[activeMatchIndex];
  const activeDraftId = activeMatch?.draftVersionId;
  const activeDraftMatchIndex = activeMatch?.index;
  const activeDraftMatch = useMemo(
    () =>
      activeDraftId && activeDraftMatchIndex !== undefined
        ? { draftVersionId: activeDraftId, index: activeDraftMatchIndex }
        : null,
    [activeDraftId, activeDraftMatchIndex],
  );
  // Marks in the transcript are numbered without the draft matches.
  const activeTranscriptMatchIndex = activeDraftId
    ? -1
    : matchIndices.slice(0, activeMatchIndex).filter((match) => !match.draftVersionId).length;

  const openerRef = useRef<HTMLElement | null>(null);
  const shouldFocusSearchButtonRef = useRef(false);

  const openSearch = useCallback(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setIsSearchOpen(true);
  }, []);
  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setActiveMatchIndex(0);
  }, [setSearchQuery]);

  const dismissSearch = useCallback(() => {
    const opener = openerRef.current;
    closeSearch();
    // The narrow header's "…" button stays mounted; the search button comes back on the next render.
    if (opener && opener !== document.body && opener.isConnected) {
      opener.focus();
    } else {
      shouldFocusSearchButtonRef.current = true;
    }
  }, [closeSearch]);

  const attachSearchButton = useCallback((node: HTMLButtonElement | null) => {
    if (!node || !shouldFocusSearchButtonRef.current) return;
    shouldFocusSearchButtonRef.current = false;
    node.focus();
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, [setSearchQuery]);

  const goToNextMatch = useCallback(() => {
    if (totalMatches === 0) return;
    setActiveMatchIndex((i) => (Math.min(i, totalMatches - 1) + 1) % totalMatches);
  }, [totalMatches]);

  const goToPrevMatch = useCallback(() => {
    if (totalMatches === 0) return;
    setActiveMatchIndex((i) => (Math.min(i, totalMatches - 1) - 1 + totalMatches) % totalMatches);
  }, [totalMatches]);

  const openDraftForMatch = useEventCallback((draftVersionId: string) => onActiveDraftMatch?.(draftVersionId));

  // Runs on every move, so stepping to a match reopens a draft the user closed.
  useEffect(() => {
    if (activeDraftId) openDraftForMatch(activeDraftId);
  }, [activeDraftId, activeMatchIndex, searchQuery, openDraftForMatch]);

  useEffect(() => {
    if (!scrollContainerRef.current || totalMatches === 0) return;

    const container = scrollContainerRef.current;
    const frame = requestAnimationFrame(() => {
      const target = applyActiveSearchMatch(container, activeTranscriptMatchIndex);
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    return () => cancelAnimationFrame(frame);
  }, [activeTranscriptMatchIndex, totalMatches, searchQuery, scrollContainerRef]);

  return {
    isSearchOpen,
    searchQuery,
    activeMatchIndex,
    matchIndices,
    activeDraftMatch,
    hasMatches,
    totalMatches,
    openSearch,
    closeSearch,
    dismissSearch,
    attachSearchButton,
    setSearchQuery,
    goToNextMatch,
    goToPrevMatch,
    clearSearch,
  };
}
